import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CreatableSelectOption {
  value: string;
  label: string;
  icon?: string;
}

export interface CreatableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: CreatableSelectOption[];
  placeholder?: string;
  onCreate: (name: string) => Promise<string>;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-required'?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

export function CreatableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select an option...",
  onCreate,
  disabled = false,
  className,
  id,
  'aria-required': ariaRequired,
  'aria-describedby': ariaDescribedby,
  'aria-invalid': ariaInvalid,
}: CreatableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [searchValue, setSearchValue] = React.useState("");

  const handleSelect = async (selectedValue: string) => {
    // Check if this is a create action
    if (selectedValue.startsWith("__create__:")) {
      const newName = selectedValue.replace("__create__:", "");
      setIsCreating(true);
      setError(null);

      try {
        const newId = await onCreate(newName);
        onValueChange(newId);
        setOpen(false);
        setSearchValue("");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to create option");
      } finally {
        setIsCreating(false);
      }
    } else {
      onValueChange(selectedValue);
      setOpen(false);
      setSearchValue("");
    }
  };

  // Find the selected option to display
  const selectedOption = options.find((opt) => opt.value === value);

  // Check if search value matches any existing option
  const hasExactMatch = options.some(
    (opt) => opt.label.toLowerCase() === searchValue.toLowerCase()
  );

  const showCreateOption = searchValue.trim() !== "" && !hasExactMatch;

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            id={id}
            aria-expanded={open}
            aria-required={ariaRequired}
            aria-describedby={ariaDescribedby}
            aria-invalid={ariaInvalid}
            className={cn(
              "w-full justify-between",
              !value && "text-muted-foreground",
              error && "border-destructive"
            )}
            disabled={disabled || isCreating}
          >
            {isCreating ? (
              <span className="text-muted-foreground">Creating...</span>
            ) : selectedOption ? (
              <span className="flex items-center gap-2">
                {selectedOption.icon && <span>{selectedOption.icon}</span>}
                {selectedOption.label}
              </span>
            ) : (
              placeholder
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or type to create..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>
                {searchValue.trim() === "" ? "Start typing to search..." : "No results found."}
              </CommandEmpty>
              <CommandGroup>
                {options
                  .filter((option) =>
                    option.label.toLowerCase().includes(searchValue.toLowerCase())
                  )
                  .map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleSelect(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex items-center gap-2">
                        {option.icon && <span>{option.icon}</span>}
                        {option.label}
                      </span>
                    </CommandItem>
                  ))}
                {showCreateOption && (
                  <CommandItem
                    value={`__create__:${searchValue}`}
                    onSelect={() => handleSelect(`__create__:${searchValue}`)}
                    className="text-primary"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create "{searchValue}"
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
